import { useMemo, useState } from 'react';
import { gql, useQuery, useSubscription } from '@apollo/client';
import dayjs from 'dayjs';
import { DATE_TIME_FORMAT_SHORT } from '../../dates';
import { v3 } from '@feedflo/tlv';

const DEVICE_DATA_GQL = gql`
  query DeviceDataQuery($deviceId: uuid!, $from: bigint!, $to: bigint!) {
    device_data(
      where: { device_id: { _eq: $deviceId }, ended_at: { _gte: $from }, started_at: { _lte: $to } }
      order_by: { ended_at: asc }
    ) {
      id
      data
      ended_at
    }
  }
`;

const DEVICE_DATA_SUB_GQL = gql`
  subscription DeviceDataSubscription($deviceId: uuid!, $from: bigint!) {
    device_data(where: { device_id: { _eq: $deviceId }, ended_at: { _gte: $from } }, order_by: { started_at: asc }) {
      id
      data
    }
  }
`;

// maxGapSeconds: Plotly line break when last sample → first new sample gap exceeds this (seconds). null = never break.
const MOTOR_CURRENT_SERIES = [
  { key: 'vibrateMotor1Current', name: 'Vibrate motor 1 (mA)', color: '#5b8def', maxGapSeconds: 10 },
  { key: 'vibrateMotor2Current', name: 'Vibrate motor 2 (mA)', color: '#7b68ee', maxGapSeconds: 10 },
  { key: 'vibrateMotor3Current', name: 'Vibrate motor 3 (mA)', color: '#4a90d9', maxGapSeconds: 10 },
  { key: 'vibrateMotor4Current', name: 'Vibrate motor 4 (mA)', color: '#6a5acd', maxGapSeconds: 10 },
  { key: 'slideMotor1Current', name: 'Slide motor 1 (mA)', color: '#26af5f', maxGapSeconds: 10 },
  { key: 'slideMotor2Current', name: 'Slide motor 2 (mA)', color: '#2ecc71', maxGapSeconds: 10 },
  { key: 'slideMotor3Current', name: 'Slide motor 3 (mA)', color: '#e67e22', maxGapSeconds: 10 },
  { key: 'slideMotor4Current', name: 'Slide motor 4 (mA)', color: '#d35400', maxGapSeconds: 10 },
];

function getMaxGapSeconds(chartKey) {
  const motorSeries = MOTOR_CURRENT_SERIES.find((s) => s.key === chartKey);
  return motorSeries?.maxGapSeconds ?? null;
}

function appendSeriesSegment(series, newX, newY, maxGapSeconds) {
  if (
    maxGapSeconds != null &&
    series.x.length > 0 &&
    newX.length > 0 &&
    series.x[series.x.length - 1] != null
  ) {
    const gapSec = (newX[0].getTime() - series.x[series.x.length - 1].getTime()) / 1000;
    if (gapSec > maxGapSeconds) {
      series.x.push(null);
      series.y.push(null);
    }
  }
  series.x.push(...newX);
  series.y.push(...newY);
}

function motorSeriesEntries(block, key) {
  const value = block?.[key];
  if (!value) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
}

function collectMotorSeriesFromTlv(tlv, key, outList) {
  motorSeriesEntries(tlv, key).forEach((series) => {
    outList.push({
      ...series,
      startTime: series.startTime ?? tlv.startTime,
      endTime: series.endTime ?? tlv.endTime,
    });
  });
  if (Array.isArray(tlv.signalDataBlocks)) {
    tlv.signalDataBlocks.forEach((block) => {
      motorSeriesEntries(block, key).forEach((series) => {
        outList.push({
          ...series,
          startTime: series.startTime ?? block.startTime,
          endTime: series.endTime ?? block.endTime,
        });
      });
    });
  }
}

export default function useSignalDataSeries({
  deviceId = '',
  to,
  from,
  skipParsing = false,
  live = false,
  skip = false,
  enableTimeOfFlight = true,
  enableRotation = true,
  enableTemperature = true,
  enableAccelerometer = true,
  enableDebugData = true,
  enableMotorCurrent = false,
}) {
  let DATA_TYPES = [];

  if (enableTimeOfFlight) DATA_TYPES = DATA_TYPES.concat(['timeOfFlight']);
  if (enableRotation) DATA_TYPES = DATA_TYPES.concat(['augerRotation']);
  if (enableTemperature) DATA_TYPES = DATA_TYPES.concat(['accelerometerTemperature']);
  if (enableTemperature) DATA_TYPES = DATA_TYPES.concat(['timeOfFlightTemperature']);
  if (enableAccelerometer) DATA_TYPES = DATA_TYPES.concat(['orientation', 'accelerometerRaw']);
  if (enableDebugData) DATA_TYPES = DATA_TYPES.concat(['debug1', 'debug2', 'debug3', 'debug4']);
  if (enableMotorCurrent) {
    DATA_TYPES = DATA_TYPES.concat(MOTOR_CURRENT_SERIES.map((s) => s.key));
  }

  const { loading, error, data } = useQuery(DEVICE_DATA_GQL, {
    variables: {
      deviceId,
      from: from.unix(),
      to: to.unix(),
    },
    skip,
  });

  const { data: subscriptionData } = useSubscription(DEVICE_DATA_SUB_GQL, {
    variables: {
      deviceId,
      from: data?.device_data?.[data?.device_data?.length - 1]?.ended_at + 1,
    },
    skip: skip || !live || !data?.device_data?.[0],
  });

  let [tlvDataErrorMessage, setTLVDataErrorMessage] = useState(null);

  const outputChartData = useMemo(() => {
    if (!(deviceId && from && to && data?.device_data?.length > 0)) return [];

    let newChartData = {};

    if (enableTimeOfFlight) {
      newChartData = {
        ...newChartData,
        timeOfFlight: {
          name: 'Time of Flight',
          x: [],
          y: [],
          line: {
            color: '#26af5f',
            width: 1,
          },
        },
      };
    }

    if (enableRotation) {
      newChartData = {
        ...newChartData,
        augerRotation: {
          name: 'Auger Rotation /s (x10)',
          x: [],
          y: [],
          line: {
            color: '#eee276',
            width: 5,
          },
        },
      };
    }

    if (enableAccelerometer) {
      newChartData = {
        ...newChartData,
        roll: {
          name: 'Roll',
          x: [],
          y: [],
          visible: 'legendonly',
        },
        pitch: {
          name: 'Pitch',
          x: [],
          y: [],
          visible: 'legendonly',
        },
        movementNoise: {
          name: 'movementNoise',
          x: [],
          y: [],
          visible: 'legendonly',
        },
        xRaw: {
          name: 'Accelerometer X Raw',
          x: [],
          y: [],
          visible: 'legendonly',
        },
        yRaw: {
          name: 'Accelerometer Y Raw',
          x: [],
          y: [],
          visible: 'legendonly',
        },
        zRaw: {
          name: 'Accelerometer Z Raw',
          x: [],
          y: [],
          visible: 'legendonly',
        },
      };
    }

    if (enableTemperature) {
      newChartData = {
        ...newChartData,
        accelerometerTemperature: {
          name: 'Acc Temperature °C',
          x: [],
          y: [],
        },
        timeOfFlightTemperature: {
          name: 'ToF Temperature °C',
          x: [],
          y: [],
        },
      };
    }

    if (enableDebugData) {
      newChartData = {
        ...newChartData,
        debug1: {
          name: 'debug1',
          x: [],
          y: [],
          yaxis: 'y2',
          visible: 'legendonly',
        },
        debug2: {
          name: 'debug2',
          x: [],
          y: [],
          yaxis: 'y2',
          visible: 'legendonly',
        },
        debug3: {
          name: 'debug3',
          x: [],
          y: [],
          yaxis: 'y2',
          visible: 'legendonly',
        },
        debug4: {
          name: 'debug4',
          x: [],
          y: [],
          yaxis: 'y2',
          visible: 'legendonly',
        },
      };
    }

    if (enableMotorCurrent) {
      MOTOR_CURRENT_SERIES.forEach(({ key, name, color }) => {
        newChartData[key] = {
          name,
          x: [],
          y: [],
          line: { color, width: 1 },
        };
      });
    }

    if (!skipParsing) {
      const dataHistory = data?.device_data || [];
      const liveData = subscriptionData?.device_data || [];

      // First extract all TLV data and organize by type
      const tlvDataByType = {};
      DATA_TYPES.forEach((type) => {
        tlvDataByType[type] = [];
      });

      [...dataHistory, ...liveData]?.forEach((curr) => {
        const hex = curr.data.substring(2);
        const arrayBuffer = new ArrayBuffer(hex.length / 2);
        const dataView = new DataView(arrayBuffer);
        for (let c = 0; c < hex.length; c += 2) {
          const byte = parseInt(hex.substring(c, c + 2), 16);
          dataView.setUint8(c / 2, byte);
        }
        const tlv = v3.TLVtoJSON(dataView);

        DATA_TYPES.forEach((type) => {
          if (type === 'accelerometerRaw') {
            // Check signalDataBlocks for accelerometerRaw
            if (tlv.signalDataBlocks && Array.isArray(tlv.signalDataBlocks)) {
              tlv.signalDataBlocks.forEach((block) => {
                if (block.accelerometerRaw) {
                  tlvDataByType[type].push({
                    accelerometerRaw: block.accelerometerRaw,
                    startTime: block.accelerometerRaw.startTime || block.startTime || 0,
                    endTime: block.accelerometerRaw.endTime || block.endTime || 0,
                  });
                }
              });
            }
            // Check accelerometerRawArray
            if (tlv.accelerometerRawArray && Array.isArray(tlv.accelerometerRawArray)) {
              tlv.accelerometerRawArray.forEach((accelerometerRaw) => {
                tlvDataByType[type].push({
                  accelerometerRaw: accelerometerRaw,
                  startTime: accelerometerRaw.startTime || tlv.startTime || 0,
                  endTime: accelerometerRaw.endTime || tlv.endTime || 0,
                });
              });
            }
            // Also check root level
            if (tlv.accelerometerRaw) {
              tlvDataByType[type].push({
                accelerometerRaw: tlv.accelerometerRaw,
                startTime: tlv.accelerometerRaw.startTime || tlv.startTime || 0,
                endTime: tlv.accelerometerRaw.endTime || tlv.endTime || 0,
              });
            }
          } else if (MOTOR_CURRENT_SERIES.some((s) => s.key === type)) {
            collectMotorSeriesFromTlv(tlv, type, tlvDataByType[type]);
          } else if (tlv[type]) {
            tlvDataByType[type].push({
              ...tlv[type],
              startTime: tlv[type].startTime ?? tlv.startTime,
              endTime: tlv[type].endTime ?? tlv.endTime,
            });
          }
        });
      });

      // Sort each type's data by startTime
      Object.keys(tlvDataByType).forEach((type) => {
        tlvDataByType[type].sort((a, b) => a.startTime - b.startTime);
      });

      // Process each type's sorted data
      Object.entries(tlvDataByType).forEach(([type, data]) => {
        data.forEach((dataItem) => {
          const { startTime, endTime, int8Data, int16Data, uint8Data, uint16Data, uint32Data, uint64Data } = dataItem;
          const startTimeMilliseconds = startTime / 1_000;
          const endTimeMilliseconds = endTime / 1_000;
          const samplePeriodMilliseconds = endTimeMilliseconds - startTimeMilliseconds;

          // Handle accelerometerRaw type separately
          if (type === 'accelerometerRaw') {
            // Extract xRaw, yRaw, zRaw from accelerometerRaw structure
            const extractRawData = (rawData) => {
              if (!rawData) return [];
              if (Array.isArray(rawData)) return rawData;
              if (rawData.int8Data && Array.isArray(rawData.int8Data)) return rawData.int8Data;
              if (rawData.int16Data && Array.isArray(rawData.int16Data)) return rawData.int16Data;
              if (rawData.uint8Data && Array.isArray(rawData.uint8Data)) return rawData.uint8Data;
              if (rawData.uint16Data && Array.isArray(rawData.uint16Data)) return rawData.uint16Data;
              if (rawData.uint32Data && Array.isArray(rawData.uint32Data)) return rawData.uint32Data;
              return [];
            };

            const accelerometerRaw = dataItem.accelerometerRaw;
            if (accelerometerRaw) {
              const rawSignals = [
                { data: extractRawData(accelerometerRaw.xRaw), name: 'xRaw' },
                { data: extractRawData(accelerometerRaw.yRaw), name: 'yRaw' },
                { data: extractRawData(accelerometerRaw.zRaw), name: 'zRaw' },
              ];

              rawSignals.forEach((signal) => {
                const newX = [];
                const newY = [];
                if (signal?.data?.length > 0) {
                  const stepSize = samplePeriodMilliseconds / signal.data.length;
                  signal.data.forEach((v, i) => {
                    const time = new Date(Math.floor(startTimeMilliseconds + stepSize * i));
                    newX.push(time);
                    newY.push(v);
                  });

                  if (newChartData[signal.name]) {
                    newChartData[signal.name].x.push(...newX);
                    newChartData[signal.name].y.push(...newY);
                  }
                }
              });
            }
            return;
          }

          // Handle orientation type separately
          if (type === 'orientation') {
            // data is an array of objects with roll, pitch, movementNoise arrays
            const orientationTimes = [
              { data: [].concat(...data.map((d) => d.startTime)), name: 'startTime' },
              { data: [].concat(...data.map((d) => d.endTime)), name: 'endTime' },
            ];
            const orientationSignals = [
              { data: [].concat(...data.map((d) => d.roll)), name: 'roll' },
              { data: [].concat(...data.map((d) => d.pitch)), name: 'pitch' },
              { data: [].concat(...data.map((d) => d.movementNoise)), name: 'movementNoise' },
            ];

            orientationSignals.forEach((signal) => {
              const newX = [];
              const newY = [];
              if (signal?.data?.length > 0) {
                // Use the actual start/end times from orientationTimes
                const startTimes = orientationTimes[0].data; // startTime array

                signal.data.forEach((v, i) => {
                  // Use the corresponding start time for this data point
                  const time = new Date(startTimes[i] / 1_000);
                  newX.push(time);
                  newY.push(v);
                });

                newChartData[signal.name].x.push(...newX);
                newChartData[signal.name].y.push(...newY);
              }
            });
            return;
          }

          const newX = [];
          const newY = [];
          // Convert signed integers if present, otherwise use unsigned
          let signalData = null;
          if (int8Data && Array.isArray(int8Data)) {
            // Convert signed 8-bit integers (values >= 128 become negative)
            signalData = int8Data.map((v) => (v > 127 ? v - 256 : v));
          } else if (int16Data && Array.isArray(int16Data)) {
            // Convert signed 16-bit integers (values >= 32768 become negative)
            signalData = int16Data.map((v) => (v > 32767 ? v - 65536 : v));
          } else {
            signalData = uint8Data || uint16Data || uint32Data || uint64Data;
          }

          if (signalData?.length > 0) {
            const stepSize = samplePeriodMilliseconds / signalData.length;
            signalData.forEach((v, i) => {
              const time = new Date(Math.floor(startTimeMilliseconds + stepSize * i));
              let divisor = 1;
              if (type === 'timeOfFlightTemperature') {
                divisor = 100;
              }
              newX.push(time);
              newY.push(v / divisor);
            });

            if (MOTOR_CURRENT_SERIES.some((s) => s.key === type)) {
              appendSeriesSegment(newChartData[type], newX, newY, getMaxGapSeconds(type));
            } else {
              newChartData[type].x.push(...newX);
              newChartData[type].y.push(...newY);
            }
          } else if (!MOTOR_CURRENT_SERIES.some((s) => s.key === type)) {
            try {
              const startErrorString = dayjs.tz(startTime / 1_000)?.format(DATE_TIME_FORMAT_SHORT);
              const endErrorString = dayjs.tz(endTime / 1_000)?.format(DATE_TIME_FORMAT_SHORT);
              setTLVDataErrorMessage(
                `${tlvDataErrorMessage}Corrupt data in TLV ${type} data. TLV start: ${startErrorString} (${startTime}). TLV end: ${endErrorString} (${endTime}).`,
              );
            } catch (e) {
              setTLVDataErrorMessage(
                `${tlvDataErrorMessage}Corrupt data in TLV ${type} data. TLV start: ${startTime} TLV end: ${endTime}. `,
              );
            }
          }
        });
      });
    }

    return [...Object.values(newChartData)];
  }, [
    data?.device_data,
    subscriptionData?.device_data,
    deviceId,
    from,
    to,
    skipParsing,
    enableTimeOfFlight,
    enableRotation,
    enableTemperature,
    enableAccelerometer,
    enableDebugData,
    enableMotorCurrent,
  ]);

  return {
    loading,
    error: error || tlvDataErrorMessage,
    data: outputChartData,
  };
}
